import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { withOwner } from "@/lib/supabase/withOwner";
import { usePatients, useSubstances } from "@/lib/usePatients";
import { calculateAlcoholDose, formatAlcoholDose, type AlcoholConsumedUnit } from "@/domain/alcoholDose";
import { calculatePowderDose, formatPowderDose, isCocaine, type PurityStatus } from "@/domain/powderDose";
import { cloneMaterialProfileSnapshot, interpretMaterialProfile, type MaterialProfile } from "@/domain/materialComposition";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, ArrowRight, Check, Clock3, GlassWater, Pill, Search } from "lucide-react";
import { toast } from "@/lib/safeToast";
import { mirrorDoseLogEvent } from "@/lib/clinical/eventPersistence";

function localNow() {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60_000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 16);
}

function asNumber(value: string) {
  if (!value.trim()) return null;
  const parsed = Number(value.replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
}

function displayName(item: any) {
  return item?.substances?.name ?? item?.free_text_name ?? item?.brand_name ?? item?.name ?? "Substância";
}

function normalized(value: string) {
  return value.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

function isAlcoholName(value: string) {
  return /(^|\s)(alcool|alcohol|etanol|ethanol)(\s|$)/.test(normalized(value));
}

const ALCOHOL_UNITS: Array<{ value: AlcoholConsumedUnit; label: string }> = [
  { value: "mL", label: "mL" },
  { value: "L", label: "L" },
  { value: "percent_container", label: "% da embalagem" },
  { value: "fraction_container", label: "fração" },
  { value: "containers", label: "embalagem(ns)" },
];

export function QuickIngestionWizard({
  open,
  onOpenChange,
  patientId,
  sessionId,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  sessionId?: string | null;
  onSaved?: () => void;
}) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data: substances = [] } = useSubstances();
  const { data: patients = [] } = usePatients();
  const patient = (patients as any[]).find((row) => row.id === patientId);
  const [step, setStep] = useState(1);
  const [search, setSearch] = useState("");
  const [selectedMedication, setSelectedMedication] = useState<any | null>(null);
  const [selectedSubstance, setSelectedSubstance] = useState<any | null>(null);
  const [dose, setDose] = useState("");
  const [unit, setUnit] = useState("mg");
  const [route, setRoute] = useState("oral");
  const [actualTime, setActualTime] = useState(localNow());
  const [logType, setLogType] = useState("manutencao");
  const [stomach, setStomach] = useState("desconhecido");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [beverageName, setBeverageName] = useState("");
  const [beverageContainerMl, setBeverageContainerMl] = useState("");
  const [beverageAbv, setBeverageAbv] = useState("");
  const [beverageConsumed, setBeverageConsumed] = useState("");
  const [beverageConsumedUnit, setBeverageConsumedUnit] = useState<AlcoholConsumedUnit>("mL");
  const [purityStatus, setPurityStatus] = useState<PurityStatus>("unknown");
  const [purityPercent, setPurityPercent] = useState("");
  const [puritySource, setPuritySource] = useState("");
  const [materialProfileId, setMaterialProfileId] = useState("");
  const [noteOrigin, setNoteOrigin] = useState<"clinician" | "patient">("clinician");

  const { data: medications = [] } = useQuery({
    queryKey: ["quick-ingestion-medications", patientId],
    enabled: open && !!patientId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("patient_medications")
        .select("*, substances(*), substance_formulations(*)")
        .eq("patient_id", patientId)
        .is("deleted_at", null)
        .order("usual_time", { ascending: true });
      if (error) throw error;
      return (data ?? []).filter((item: any) => !/suspens|descontinu|encerr/i.test(String(item.status ?? "")));
    },
  });

  const { data: recentDoses = [] } = useQuery({
    queryKey: ["quick-ingestion-history", patientId],
    enabled: open && !!patientId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("medication_dose_logs")
        .select("*")
        .eq("patient_id", patientId)
        .is("deleted_at", null)
        .order("actual_time", { ascending: false })
        .limit(150);
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: materialProfiles = [], error: materialProfilesError } = useQuery({
    queryKey: ["quick-material-profiles", user?.id, patientId],
    enabled: open && !!user?.id && !!patientId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("substance_material_profiles")
        .select("*")
        .eq("owner_id", user!.id)
        .eq("is_active", true)
        .is("deleted_at", null)
        .or("patient_id.is.null,patient_id.eq." + patientId)
        .order("updated_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as MaterialProfile[];
    },
  });

  const visibleSubstances = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return substances.slice(0, 24);
    return substances.filter((item: any) => {
      const haystack = [item.name, item.aliases, item.brand_names, item.drug_class]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(needle);
    }).slice(0, 40);
  }, [substances, search]);

  const selectedName = displayName(selectedMedication ?? selectedSubstance);
  const canonicalSelectedName = selectedMedication?.substances?.name ?? selectedSubstance?.name ?? selectedName;
  const matchingMaterialProfiles = useMemo(
    () => (materialProfiles as MaterialProfile[]).filter((profile) => norm(profile.canonical_substance_name) === norm(canonicalSelectedName)),
    [materialProfiles, canonicalSelectedName],
  );
  const selectedMaterialProfile = (materialProfiles as MaterialProfile[]).find((profile) => profile.id === materialProfileId) ?? null;
  const isAlcohol = isAlcoholName(selectedName);
  const isPowder = isCocaine(selectedName);
  const powderDose = useMemo(
    () => isPowder ? calculatePowderDose(asNumber(dose) ?? 0, unit, purityStatus, asNumber(purityPercent)) : null,
    [isPowder, dose, unit, purityStatus, purityPercent],
  );
  const materialProfileForDisplay: MaterialProfile | null = selectedMaterialProfile ? {
    ...selectedMaterialProfile,
    purity_status: isPowder ? purityStatus : selectedMaterialProfile.purity_status,
    purity_percent: isPowder ? (purityStatus === "unknown" ? null : asNumber(purityPercent)) : selectedMaterialProfile.purity_percent,
    purity_method: isPowder ? (puritySource || selectedMaterialProfile.purity_method || null) : selectedMaterialProfile.purity_method,
  } : null;
  const recentBeverages = useMemo(() => {
    const seen = new Set<string>();
    return (recentDoses as any[]).filter((row) => {
      if (!(Number(row.beverage_container_volume_ml) > 0) || row.beverage_abv_percent == null) return false;
      const key = `${row.beverage_name ?? "Bebida"}:${row.beverage_container_volume_ml}:${row.beverage_abv_percent}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }).slice(0, 6);
  }, [recentDoses]);

  const alcoholDose = useMemo(() => {
    if (!isAlcohol) return null;
    const containerVolumeMl = asNumber(beverageContainerMl);
    const abvPercent = asNumber(beverageAbv);
    const consumedValue = asNumber(beverageConsumed);
    if (containerVolumeMl == null || abvPercent == null || consumedValue == null) return null;
    return calculateAlcoholDose({ containerVolumeMl, abvPercent, consumedValue, consumedUnit: beverageConsumedUnit });
  }, [isAlcohol, beverageContainerMl, beverageAbv, beverageConsumed, beverageConsumedUnit]);

  const quickDoses = useMemo(() => {
    if (isAlcohol || isPowder) return [];
    const values = new Map<string, { amount: number; unit: string }>();
    if (selectedMedication?.current_dose != null) {
      const amount = Number(selectedMedication.current_dose);
      if (Number.isFinite(amount)) values.set(`${amount}:${selectedMedication.dose_unit ?? "mg"}`, { amount, unit: selectedMedication.dose_unit ?? "mg" });
    }
    const substanceId = selectedMedication?.substance_id ?? selectedSubstance?.id;
    const medicationId = selectedMedication?.id;
    for (const item of recentDoses) {
      if (medicationId && item.patient_medication_id !== medicationId && item.substance_id !== substanceId) continue;
      if (!medicationId && substanceId && item.substance_id !== substanceId) continue;
      const amount = Number(item.dose_amount);
      if (!Number.isFinite(amount)) continue;
      const itemUnit = item.dose_unit ?? "mg";
      values.set(`${amount}:${itemUnit}`, { amount, unit: itemUnit });
      if (values.size >= 6) break;
    }
    return Array.from(values.values());
  }, [isAlcohol, isPowder, selectedMedication, selectedSubstance, recentDoses]);

  function chooseMedication(item: any) {
    setBeverageName("");
    setBeverageContainerMl("");
    setBeverageAbv("");
    setBeverageConsumed("");
    setPurityStatus("unknown");
    setPurityPercent("");
    setPuritySource("");
    setMaterialProfileId("");
    setSelectedMedication(item);
    setSelectedSubstance(item.substances ?? null);
    const name = displayName(item);
    if (isAlcoholName(name)) {
      setDose("");
      setUnit("g");
      setRoute("oral");
    } else {
      setDose(item.current_dose == null ? "" : String(item.current_dose));
      setUnit(item.dose_unit ?? item.substances?.default_dose_unit ?? "mg");
      setRoute(item.substance_formulations?.route ?? item.substances?.default_route ?? "oral");
    }
    setStep(2);
  }

  function chooseSubstance(item: any) {
    setBeverageName("");
    setBeverageContainerMl("");
    setBeverageAbv("");
    setBeverageConsumed("");
    setPurityStatus("unknown");
    setPurityPercent("");
    setPuritySource("");
    setMaterialProfileId("");
    setSelectedMedication(null);
    setSelectedSubstance(item);
    const name = displayName(item);
    setDose("");
    setUnit(isAlcoholName(name) ? "g" : item.default_dose_unit ?? item.dose_unit_default ?? "mg");
    setRoute(isAlcoholName(name) ? "oral" : item.default_route ?? "oral");
    setStep(2);
  }

  function reset() {
    setStep(1);
    setSearch("");
    setSelectedMedication(null);
    setSelectedSubstance(null);
    setDose("");
    setUnit("mg");
    setRoute("oral");
    setActualTime(localNow());
    setLogType("manutencao");
    setStomach("desconhecido");
    setNote("");
    setBeverageName("");
    setBeverageContainerMl("");
    setBeverageAbv("");
    setBeverageConsumed("");
    setBeverageConsumedUnit("mL");
    setPurityStatus("unknown");
    setPurityPercent("");
    setPuritySource("");
    setMaterialProfileId("");
    setNoteOrigin("clinician");
  }

  async function save() {
    if (saving) return;
    if (!user) return toast.error("Você precisa estar autenticado.");
    if (!patientId || !patient) return toast.error("Selecione um paciente válido.");
    const substanceName = selectedName;
    const amount = isAlcohol
      ? alcoholDose?.ethanolGrams ?? null
      : isPowder
        ? powderDose?.estimated_active_mg ?? null
        : asNumber(dose);
    const finalUnit = isAlcohol ? "g" : isPowder ? (amount == null ? null : "mg") : unit;
    const finalRoute = isAlcohol ? "oral" : route;
    if (!substanceName || substanceName === "Substância") return toast.error("Selecione uma substância.");
    if (isAlcohol && amount == null) return toast.error("Preencha volume, teor alcoólico e quantidade consumida.");
    if (isPowder && !powderDose) return toast.error("Informe uma massa válida em mg ou g e confira a pureza.");
    if (isPowder && purityStatus !== "unknown" && !puritySource.trim()) return toast.error("Informe a origem/método da pureza estimada ou analisada.");
    if (!isAlcohol && !isPowder && (amount == null || amount <= 0)) return toast.error("Informe uma dose numérica positiva.");
    if (!Number.isFinite(new Date(actualTime).getTime())) return toast.error("Informe data e horário válidos.");
    const materialProfileForSnapshot: MaterialProfile | null = selectedMaterialProfile ? {
      ...selectedMaterialProfile,
      purity_status: isPowder ? purityStatus : selectedMaterialProfile.purity_status,
      purity_percent: isPowder ? (purityStatus === "unknown" ? null : asNumber(purityPercent)) : selectedMaterialProfile.purity_percent,
      purity_method: isPowder ? (puritySource || selectedMaterialProfile.purity_method || null) : selectedMaterialProfile.purity_method,
    } : null;
    const materialProfileSnapshot = cloneMaterialProfileSnapshot(materialProfileForSnapshot);
    const materialProfileRiskInterpretation = interpretMaterialProfile(materialProfileForSnapshot, powderDose?.powder_mass_mg ?? null);
    setSaving(true);
    try {
      const doseText = isAlcohol && alcoholDose
        ? formatAlcoholDose(alcoholDose)
        : isPowder && powderDose
          ? formatPowderDose(powderDose)
          : `${amount} ${finalUnit}`.trim();
      const payload = withOwner({
        patient_id: patientId,
        session_id: sessionId ?? null,
        patient_medication_id: selectedMedication?.id ?? null,
        substance_id: selectedMedication?.substance_id ?? selectedSubstance?.id ?? null,
        substance_name: substanceName,
        formulation_id: selectedMedication?.formulation_id ?? null,
        formulation_name: selectedMedication?.substance_formulations?.formulation_name ?? null,
        formulation: selectedMedication?.substance_formulations?.formulation_name ?? null,
        dose_amount: amount,
        dose_unit: finalUnit || null,
        dose_text: doseText,
        route: finalRoute || null,
        actual_time: new Date(actualTime).toISOString(),
        planned_time: null,
        log_type: logType,
        stomach: stomach || null,
        taken_with_food: stomach === "desconhecido" ? null : stomach !== "vazio",
        notes_patient: noteOrigin === "patient" ? note || null : null,
        notes_clinician: noteOrigin === "clinician" ? note || null : null,
        beverage_name: isAlcohol ? beverageName.trim() || null : null,
        beverage_container_volume_ml: isAlcohol ? alcoholDose?.containerVolumeMl ?? null : null,
        beverage_abv_percent: isAlcohol ? alcoholDose?.abvPercent ?? null : null,
        beverage_consumed_value: isAlcohol ? alcoholDose?.consumedValue ?? null : null,
        beverage_consumed_unit: isAlcohol ? alcoholDose?.consumedUnit ?? null : null,
        beverage_consumed_ml: isAlcohol ? alcoholDose?.consumedMl ?? null : null,
        ethanol_grams: isAlcohol ? alcoholDose?.ethanolGrams ?? null : null,
        powder_mass_mg: isPowder ? powderDose?.powder_mass_mg ?? null : null,
        purity_status: isPowder ? powderDose?.purity_status ?? "unknown" : null,
        purity_percent: isPowder ? powderDose?.purity_percent ?? null : null,
        purity_source: isPowder && purityStatus !== "unknown" ? puritySource.trim() || null : null,
        estimated_active_mg: isPowder ? powderDose?.estimated_active_mg ?? null : null,
        material_profile_id: materialProfileForSnapshot?.id ?? null,
        material_profile_snapshot: materialProfileSnapshot,
        material_profile_risk_interpretation: materialProfileRiskInterpretation,
      }, user);
      const { data: savedDose, error } = await (supabase as any)
        .from("medication_dose_logs")
        .insert(payload)
        .select("*")
        .single();
      if (error) throw error;
      try {
        await mirrorDoseLogEvent(user, savedDose);
      } catch (mirrorError) {
        console.warn("[QuickIngestionWizard] timeline mirror failed", mirrorError);
      }
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["session-doses", sessionId] }),
        qc.invalidateQueries({ queryKey: ["my-space-doses", patientId] }),
        qc.invalidateQueries({ queryKey: ["recent-doses", patientId] }),
        qc.invalidateQueries({ queryKey: ["journal-doses", patientId] }),
        qc.invalidateQueries({ queryKey: ["psylog-events", patientId] }),
        qc.invalidateQueries({ queryKey: ["journal-body-load", patientId] }),
        qc.invalidateQueries({ queryKey: ["insights-doses", patientId] }),
        qc.invalidateQueries({ queryKey: ["insights-body-load", patientId] }),
        qc.invalidateQueries({ queryKey: ["curves-doses", patientId] }),
        qc.invalidateQueries({ queryKey: ["all-doses"] }),
        qc.invalidateQueries({ queryKey: ["quick-ingestion-history", patientId] }),
        qc.invalidateQueries({ queryKey: ["quick-material-profiles", user.id, patientId] }),
        qc.invalidateQueries({ queryKey: ["journal-day-context", patientId] }),
      ]);
      toast.success(
        isAlcohol
          ? sessionId
            ? "Bebida registrada em gramas de etanol e vinculada à sessão."
            : "Bebida registrada em gramas de etanol no prontuário longitudinal."
          : sessionId
            ? "Ingestão registrada, vinculada à sessão e curva atualizada."
            : "Ingestão registrada no prontuário longitudinal e curva atualizada.",
      );
      onSaved?.();
      onOpenChange(false);
      reset();
    } catch (error: any) {
      toast.error(error?.message ?? "Falha ao registrar ingestão.");
    } finally {
      setSaving(false);
    }
  }

  const doseReady = isAlcohol
    ? !!alcoholDose && alcoholDose.ethanolGrams > 0
    : isPowder
      ? !!powderDose && (purityStatus === "unknown" || !!puritySource.trim())
      : (asNumber(dose) ?? 0) > 0 && !!unit.trim();
  const confirmation = isAlcohol && alcoholDose
    ? formatAlcoholDose(alcoholDose)
    : isPowder && powderDose
      ? formatPowderDose(powderDose)
      : `${dose || "—"} ${unit}`.trim();

  return (
    <Dialog open={open} onOpenChange={(next) => { onOpenChange(next); if (!next) reset(); }}>
      <DialogContent className="h-[100dvh] max-h-[100dvh] w-[calc(100vw-0.75rem)] max-w-[760px] rounded-2xl border p-0 sm:h-auto sm:max-h-[92vh] sm:w-[min(760px,94vw)]">
        <div className="flex h-full min-h-0 flex-col">
          <DialogHeader className="border-b px-4 pb-3 pt-[max(1rem,env(safe-area-inset-top))] sm:px-5 sm:pt-5">
            <div className="flex items-center justify-between gap-3">
              <div>
                <DialogTitle className="text-xl">Registrar dose / ingestão</DialogTitle>
                <div className="mt-1 text-sm font-medium">Paciente: {patient?.social_name || patient?.full_name || "não selecionado"}</div>
                <div className="mt-1 text-xs text-muted-foreground">Etapa {step} de 3 · sessão opcional · o registro aparece no Journal mesmo sem sessão</div>
              </div>
              <Badge variant="outline">{selectedName}</Badge>
            </div>
          </DialogHeader>

          <div className="min-h-0 flex-1 overflow-y-auto p-4 pb-28 sm:p-5 sm:pb-5">
            {step === 1 ? (
              <div className="space-y-4">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar substância, marca ou classe" className="h-11 pl-9" />
                </div>
                {medications.length ? (
                  <section className="space-y-2">
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Tratamento ativo</div>
                    <div className="divide-y overflow-hidden rounded-2xl border">
                      {medications.map((item: any) => (
                        <button key={item.id} type="button" className="flex w-full items-center gap-3 p-3 text-left hover:bg-muted/50" onClick={() => chooseMedication(item)}>
                          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-primary/10 text-primary"><Pill className="h-5 w-5" /></span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate font-medium">{displayName(item)}</span>
                            <span className="block truncate text-xs text-muted-foreground">{[item.brand_name, item.current_dose != null ? `${item.current_dose} ${item.dose_unit ?? ""}`.trim() : null, item.substance_formulations?.formulation_name].filter(Boolean).join(" · ")}</span>
                          </span>
                          <ArrowRight className="h-4 w-4 text-muted-foreground" />
                        </button>
                      ))}
                    </div>
                  </section>
                ) : null}
                <section className="space-y-2">
                  <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Biblioteca de substâncias</div>
                  <div className="divide-y overflow-hidden rounded-2xl border">
                    {visibleSubstances.map((item: any) => (
                      <button key={item.id} type="button" className="flex w-full items-center gap-3 p-3 text-left hover:bg-muted/50" onClick={() => chooseSubstance(item)}>
                        <span className="h-3 w-3 shrink-0 rounded-full bg-primary" />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-medium">{item.name}</span>
                          <span className="block truncate text-xs text-muted-foreground">{[item.aliases, item.brand_names, item.drug_class].filter(Boolean).join(" · ")}</span>
                        </span>
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                      </button>
                    ))}
                  </div>
                </section>
              </div>
            ) : null}

            {step === 2 ? (
              <div className="space-y-5">
                <div className="rounded-2xl border bg-muted/20 p-4">
                  <div className="flex items-center gap-2 font-semibold">{isAlcohol ? <GlassWater className="h-4 w-4 text-primary" /> : null}{selectedName}</div>
                  <div className="mt-1 text-xs text-muted-foreground">{isAlcohol ? "Registre a bebida; o ClinicalOS converte o volume consumido em massa de etanol." : "Escolha uma dose usada antes ou informe outra."}</div>
                  {!isAlcohol && quickDoses.length ? <div className="mt-3 flex flex-wrap gap-2">{quickDoses.map((item) => <Button key={`${item.amount}-${item.unit}`} type="button" variant={dose === String(item.amount) && unit === item.unit ? "default" : "outline"} onClick={() => { setDose(String(item.amount)); setUnit(item.unit); }}>{item.amount} {item.unit}</Button>)}</div> : null}
                </div>

                {isAlcohol ? (
                  <div className="space-y-4">
                    <div className="space-y-1.5">
                      <Label>Bebida / rótulo</Label>
                      <Input value={beverageName} onChange={(event) => setBeverageName(event.target.value)} placeholder="Nome da bebida ou marca" />
                    </div>
                    {recentBeverages.length ? (
                      <div className="flex flex-wrap gap-2">
                        {recentBeverages.map((row: any) => (
                          <Button key={row.id} type="button" size="sm" variant="outline" onClick={() => {
                            setBeverageName(row.beverage_name ?? "");
                            setBeverageContainerMl(String(row.beverage_container_volume_ml));
                            setBeverageAbv(String(row.beverage_abv_percent));
                          }}>
                            {row.beverage_name || "Bebida anterior"} · {row.beverage_container_volume_ml} mL · {row.beverage_abv_percent}%
                          </Button>
                        ))}
                      </div>
                    ) : null}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5"><Label>Volume da embalagem</Label><div className="relative"><Input inputMode="decimal" value={beverageContainerMl} onChange={(event) => setBeverageContainerMl(event.target.value)} placeholder="250" className="pr-12" /><span className="pointer-events-none absolute right-3 top-2.5 text-sm text-muted-foreground">mL</span></div></div>
                      <div className="space-y-1.5"><Label>Teor alcoólico</Label><div className="relative"><Input inputMode="decimal" value={beverageAbv} onChange={(event) => setBeverageAbv(event.target.value)} placeholder="7,5" className="pr-14" /><span className="pointer-events-none absolute right-3 top-2.5 text-sm text-muted-foreground">% ABV</span></div></div>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Quanto foi consumido</Label>
                      <div className="grid grid-cols-[minmax(0,1fr)_150px] gap-2">
                        <Input inputMode="decimal" value={beverageConsumed} onChange={(event) => setBeverageConsumed(event.target.value)} placeholder="15" />
                        <select className="h-10 min-w-0 rounded-md border border-input bg-background px-2 text-sm" value={beverageConsumedUnit} onChange={(event) => setBeverageConsumedUnit(event.target.value as AlcoholConsumedUnit)}>{ALCOHOL_UNITS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select>
                      </div>
                    </div>
                    <Card className="rounded-2xl border-primary/20 bg-primary/5 p-4">
                      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Etanol calculado</div>
                      {alcoholDose ? (
                        <><div className="mt-1 text-2xl font-semibold tabular-nums">{alcoholDose.ethanolGrams.toFixed(alcoholDose.ethanolGrams >= 10 ? 1 : 2)} g</div><div className="mt-1 text-xs text-muted-foreground">{alcoholDose.consumedMl.toFixed(alcoholDose.consumedMl >= 100 ? 0 : 1)} mL de bebida · {alcoholDose.ethanolMl.toFixed(2)} mL de etanol puro</div></>
                      ) : <div className="mt-2 text-sm text-muted-foreground">Preencha os três campos para calcular.</div>}
                      <div className="mt-3 text-[10px] leading-relaxed text-muted-foreground">Cálculo: volume consumido × ABV × densidade do etanol. Isso estima massa ingerida; não estima alcoolemia.</div>
                    </Card>
                  </div>
                ) : (
                  <>

                    <Card className="space-y-2 rounded-xl border-primary/20 p-3">
                      <div className="text-sm font-medium">Perfil de material / amostra (opcional)</div>
                      <select className="h-10 w-full min-w-0 rounded-md border border-input bg-background px-3 text-sm" value={materialProfileId} onChange={(event) => {
                        const id = event.target.value;
                        setMaterialProfileId(id);
                        const profile = (materialProfiles as MaterialProfile[]).find((item) => item.id === id);
                        if (profile && isCocaine(selectedName)) {
                          setPurityStatus(profile.purity_status ?? "unknown");
                          setPurityPercent(profile.purity_percent == null ? "" : String(profile.purity_percent));
                          setPuritySource(profile.purity_method ?? "");
                        }
                      }}>
                        <option value="">Sem perfil salvo — registrar apenas esta exposição</option>
                        {matchingMaterialProfiles.map((profile) => <option key={profile.id} value={profile.id}>{profile.profile_name}{profile.sample_label ? " · " + profile.sample_label : ""}</option>)}
                      </select>
                      {materialProfilesError ? <div className="text-[11px] text-muted-foreground">Perfis não carregaram. Confirme se a migration de perfis de material foi aplicada.</div> : null}
                      {matchingMaterialProfiles.length === 0 ? <div className="text-[11px] text-muted-foreground">Nenhum perfil para este canônico. Crie no Laboratório PK/PD → Perfis de material; você pode cadastrar várias amostras sem duplicar o canônico.</div> : null}
                      {selectedMaterialProfile ? (() => {
                        const interpretation = interpretMaterialProfile(materialProfileForDisplay, powderDose?.powder_mass_mg ?? null);
                        return <div className="space-y-1 rounded-lg bg-muted/30 p-2 text-xs"><div className="font-medium">{interpretation?.summary}</div><div className="text-muted-foreground">Composição: {selectedMaterialProfile.composition_status} · confiança {selectedMaterialProfile.composition_confidence}. A falta de componente listado não prova ausência.</div>{(interpretation?.specificRisks ?? []).length ? <div className="text-amber-700 dark:text-amber-300">{interpretation?.specificRisks.join(" ")}</div> : null}{(interpretation?.redFlags ?? []).length ? <div className="text-destructive">Sinais de alarme a considerar: {interpretation?.redFlags.join(" ")}</div> : null}</div>;
                      })() : null}
                    </Card>
                    <div className="grid grid-cols-[1fr_110px] gap-3">
                      <div className="space-y-1.5"><Label>Dose</Label><Input inputMode="decimal" value={dose} onChange={(event) => setDose(event.target.value)} placeholder="70" /></div>
                      <div className="space-y-1.5"><Label>Unidade</Label><Input value={unit} onChange={(event) => setUnit(event.target.value)} placeholder="mg" /></div>
                    </div>
                    {isPowder ? (
                      <Card className="space-y-3 rounded-xl p-3">
                        <div className="text-sm font-medium">Material/pó · pureza</div>
                        <div className="space-y-1.5">
                          <Label>Pureza da amostra</Label>
                          <select className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={purityStatus} onChange={(event) => {
                            setPurityStatus(event.target.value as PurityStatus);
                            setPurityPercent("");
                            setPuritySource("");
                          }}>
                            <option value="unknown">Desconhecida</option>
                            <option value="estimated">Estimativa informada</option>
                            <option value="tested">Resultado de análise informado</option>
                          </select>
                        </div>
                        {purityStatus !== "unknown" ? (
                          <div className="grid gap-3 sm:grid-cols-2">
                            <div className="space-y-1.5"><Label>Pureza · %</Label><Input inputMode="decimal" value={purityPercent} onChange={(event) => setPurityPercent(event.target.value)} /></div>
                            <div className="space-y-1.5"><Label>Origem/método</Label><Input value={puritySource} onChange={(event) => setPuritySource(event.target.value)} placeholder="Relato, laudo ou teste" /></div>
                          </div>
                        ) : null}
                        <div className="text-xs text-muted-foreground">{powderDose ? formatPowderDose(powderDose) : "Informe a massa do material em mg ou g."}</div>
                        <div className="text-[10px] leading-relaxed text-muted-foreground">A aparência não determina pureza. Quando a pureza é desconhecida, o sistema registra a massa do material sem inventar massa de princípio ativo.</div>
                      </Card>
                    ) : null}
                                        <div className="space-y-1.5"><Label>Via</Label><select className="h-11 w-full rounded-md border border-input bg-background px-3" value={route} onChange={(event) => setRoute(event.target.value)}>{["oral", "sublingual", "intranasal", "inalatória", "transdérmica", "intramuscular", "intravenosa", "retal", "outra"].map((value) => <option key={value} value={value}>{value}</option>)}</select></div>
                  </>
                )}
              </div>
            ) : null}

            {step === 3 ? (
              <div className="space-y-5">
                <div className="space-y-1.5"><Label>Data e horário</Label><div className="relative"><Clock3 className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground" /><Input type="datetime-local" value={actualTime} onChange={(event) => setActualTime(event.target.value)} className="pl-9" /></div></div>
                <div className="space-y-1.5"><Label>Classificação</Label><select className="h-11 w-full rounded-md border border-input bg-background px-3" value={logType} onChange={(event) => setLogType(event.target.value)}><option value="manutencao">manutenção</option><option value="redose">redose</option><option value="prn">SOS/PRN</option><option value="atraso">atraso</option><option value="uso_problematico">uso problemático</option></select></div>
                <div className="space-y-1.5"><Label>Estômago</Label><div className="grid grid-cols-2 gap-2 sm:grid-cols-4">{[["vazio","Vazio"],["quarter_full","¼ cheio"],["half_full","½ cheio"],["full","Cheio"]].map(([value,label]) => <Button key={value} type="button" variant={stomach === value ? "default" : "outline"} onClick={() => setStomach(value)}>{label}</Button>)}</div></div>
                <div className="space-y-1.5">
                      <Label>Origem da observação</Label>
                      <select className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={noteOrigin} onChange={(event) => setNoteOrigin(event.target.value as "clinician" | "patient")}>
                        <option value="clinician">Observação médica</option>
                        <option value="patient">Relato do paciente</option>
                      </select>
                      <Label>Nota</Label><Textarea value={note} onChange={(event) => setNote(event.target.value)} rows={5} placeholder="Contexto, objetivo, sintomas, refeição, motivo da redose…" /></div>
                <div className="rounded-2xl border bg-muted/20 p-3 text-sm"><div className="font-medium">Confirmar ingestão</div><div className="mt-1 text-muted-foreground">{selectedName} · {confirmation} · {isAlcohol ? "oral" : route} · {new Date(actualTime).toLocaleString("pt-BR")}</div></div>
              </div>
            ) : null}
          </div>

          <div className="sticky bottom-0 z-10 -mx-4 mt-3 border-t bg-background/95 p-3 pb-[max(.75rem,env(safe-area-inset-bottom))] backdrop-blur sm:mx-0 sm:mt-0 sm:static sm:p-4">
            <div className="mx-auto flex max-w-3xl items-center justify-between gap-2">
              <Button type="button" variant="outline" onClick={() => step === 1 ? onOpenChange(false) : setStep((current) => current - 1)}><ArrowLeft className="mr-1 h-4 w-4" />{step === 1 ? "Cancelar" : "Voltar"}</Button>
              {step < 3 ? <Button type="button" onClick={() => setStep((current) => current + 1)} disabled={(!selectedMedication && !selectedSubstance) || (step === 2 && !doseReady)}>Continuar<ArrowRight className="ml-1 h-4 w-4" /></Button> : <Button type="button" onClick={save} disabled={saving}><Check className="mr-1 h-4 w-4" />{saving ? "Salvando…" : "Concluir"}</Button>}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
